import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { fetchData, updateData } from "../../../../services/ApiFetching";
import Card from 'components/card';
import TICKET_STATUSES from 'constants/constants';
import Modal from 'components/modal';
import Tabs from 'components/tabs';
import { FaArrowLeftLong } from "react-icons/fa6";

const TicketDetails = () => {
  const Navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const role = localStorage.getItem('role') || 'customer';
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("status");
  const [activeTab, setActiveTab] = useState('Status History');
  const [newComment, setNewComment] = useState('');
  const fromCustomer = location.state?.fromCustomer || false;
  const extra = fromCustomer ? "px-6" : "";

  const fetchTicket = async () => {
    try {
      const data = await fetchData(`tickets/${id}`);
      setTicket(data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setError(error.message || 'Failed to load ticket.');
    }
  };

  useEffect(() => {
    fetchTicket();
  }, [id]);

  const handleOpenModal = (type) => {
    setModalType(type);
    setShowModal(true);
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    const newCommentEntry = {
      text: newComment,
      commented_by: localStorage.getItem('username'),
      created_at: new Date().toISOString(),
    };

    const updatedTicketData = {
      ...ticket.data,
      comments: [...(ticket.data?.comments || []), newCommentEntry],
    };

    const updatedTicket = {
      subject: ticket.subject,
      description: ticket.description,
      status: ticket.status,
      assigned_to: ticket.assigned_to,
      priority:ticket.priority,
      data: updatedTicketData,
    };

    try {
      await updateData(`tickets/${ticket.id}`, updatedTicket);
      setTicket(prev => ({
        ...prev,
        data: updatedTicketData
      }));
      setNewComment('');
    } catch (err) {
      console.error('Failed to add comment:', err);
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div style={{ color: 'red' }}>{error}</div>;
  if (!ticket) return <div>No ticket found.</div>;

  return (
    <>
    <div className={`${extra}`}>
      <Card extra={`flex flex-col w-full my-3 p-4 bg-white`}>
        <div className="flex items-center">
          <div onClick={() => Navigate("/admin/tickets")} className="mr-3 cursor-pointer"><FaArrowLeftLong /></div>
          <p className="text-xl font-bold text-black-500 dark:text-white">View Ticket</p>
        </div>
      </Card>

      <div className='flex w-full mt-3'>
        {/* left */}
        <div className='w-full'>
          <Card extra={`flex flex-col w-full p-4 bg-white`}>
            <p className="text-xl font-bold text-black-500 dark:text-white">{ticket.subject}</p>
          </Card>

          <Card extra={`flex my-3 flex-col w-full p-4 bg-white`}>
            <p className="text-xl font-bold text-black-500 dark:text-white">Description</p>
            <p className="text-md text-black-500 dark:text-white">{ticket.description}</p>
          </Card>

          <div className='flex gap-3'>
            <Card extra="flex flex-col w-full h-100 p-4 bg-white">
              <Tabs
                tabs={['Status History', 'Agent History']}
                activeTab={activeTab}
                onTabClick={setActiveTab}
              />

              {activeTab === 'Status History' && (
                <div className="mt-3">
                  {ticket?.data?.status_history?.length > 0 ? (
                    ticket.data.status_history.map((history, index) => (
                      <div key={index} className="mb-3">
                        <p className="text-md text-gray-700 dark:text-gray-300">
                          <strong>Status :</strong> {TICKET_STATUSES.find(status => status.value === history.status)?.label || history.status}<br />
                          <strong>Changed By :</strong> {history.changed_by}<br />
                          <strong>Changed At :</strong> {new Date(history.changed_at).toLocaleString()}
                        </p>
                        <hr className="mt-2" />
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500">No Status History available.</p>
                  )}
                </div>
              )}

              {activeTab === 'Agent History' && (
                <div className="mt-4">
                  <p className="text-lg font-bold text-black-500 dark:text-white mb-2">Agent Assignment History</p>
                  {ticket?.data?.assignment_history?.length > 0 ? (
                    ticket.data.assignment_history.map((history, index) => (
                      <div key={index} className="mb-3">
                        <p className="text-md text-gray-700 dark:text-gray-300">
                          Assigned to <strong>{history.assigned_to}</strong> on{' '}
                          {new Date(history.assigned_at).toLocaleString()}
                        </p>
                        <hr className="mt-2" />
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500">No Agent Assignment History Available.</p>
                  )}
                </div>
              )}
            </Card>

            <Card extra={`flex flex-col w-full h-100 p-4 bg-white`}>
              <p className="text-xl font-bold text-black-500 dark:text-white border-b border-gray-300 dark:border-gray-700 pb-2 mb-7">
                Ticket Comments
              </p>

              {ticket?.data?.comments?.length > 0 ? (
                ticket.data.comments.map((comment, index) => (
                  <div key={index} className="mb-3">
                    <p className="text-md text-gray-700 dark:text-gray-300">
                      <strong>{new Date(comment.created_at).toLocaleString()}</strong><br />
                      <strong>{comment.commented_by} :</strong> {comment.text}
                    </p>
                    <hr className="mt-2" />
                  </div>
                ))
              ) : (
                <p className="text-gray-500">No Comments Available.</p>
              )}

              <div className="mt-5">
                <textarea
                  rows={3}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md p-2 mb-2 dark:bg-gray-800 dark:text-white"
                  placeholder="Add a comment..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                />
                <button
                  onClick={handleAddComment}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-500 transition"
                >
                  Submit Comment
                </button>
              </div>
            </Card>
          </div>
        </div>

        {/* right */}
        <div className='w-[50%] ml-3'>
          <Card extra={`flex flex-col w-full h-100 p-4 bg-white`}>
            {role !== 'customer' && (
              <div className='flex flex-col md:flex-row gap-3 items-center justify-between'>
                <button
                  onClick={() => handleOpenModal("status")}
                  className="w-full md:w-fit linear rounded-full bg-brand-900 px-4 py-3 mb-2 text-lg font-medium text-white transition duration-200 hover:bg-brand-800"
                >
                  Change Status
                </button>
                <button
                  onClick={() => handleOpenModal("agent")}
                  className="w-full md:w-fit linear rounded-full bg-brand-900 px-4 py-3 mb-2 text-lg font-medium text-white transition duration-200 hover:bg-brand-800"
                >
                  Change Agent
                </button>
                <button
                  onClick={() => handleOpenModal("priority")}
                  className="w-full md:w-fit linear rounded-full bg-brand-900 px-4 py-3 mb-2 text-lg font-medium text-white transition duration-200 hover:bg-brand-800"
                >
                  Change Priority
                </button>
              </div> 
            )}
           

            <div className='my-1'><strong>Status :</strong> {TICKET_STATUSES.find(s => s.value === ticket.status)?.label || ticket.status}</div>
            <div className='my-1'><strong>Priority :</strong> {TICKET_STATUSES.find(s => s.value === ticket.priority)?.label || ticket.priority}</div>
            <div className='my-1'><strong>Assigned To :</strong> {ticket.assigned_to_user}</div>
            <div className='my-1'><strong>Created By :</strong> {ticket.created_by_user}</div>
            <div className='my-1'><strong>Response Time :</strong> 1 Hour</div>
            <div className='my-1'><strong>Resolution Time :</strong> 2 Hour</div>
            <div className='my-1'><strong>Created On :</strong> {new Date(ticket.created_at).toLocaleString()}</div>
            <div className='my-1'><strong>SLA Breached :</strong> Yes/No</div>
            <div className='my-1'><strong>Response Time Breached By :</strong> 1 Hour</div>
            <div className='my-1'><strong>Resolution Time Breached By :</strong> 2 Hour</div>
          </Card>
        </div>
      </div>
    </div>


      {showModal && (
          <Modal
            type={modalType}
            ticket={ticket}
            onClose={() => setShowModal(false)}
            onUpdate={() => {
              fetchTicket(); // refresh ticket from server
              setShowModal(false);
            }}
          />
        )}
    </>
  );
};

export default TicketDetails;
